import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Star, StarOff, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { SupplierBrandModal } from './supplier-brand-modal';

interface SupplierBrandsCardProps {
  supplierId: string;
  supplierName: string;
}

export function SupplierBrandsCard({ supplierId, supplierName }: SupplierBrandsCardProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const { data: brands, isLoading } = trpc.tradingBrandSupplier.getBrandsBySupplier.useQuery(supplierId);

  const deleteMutation = trpc.tradingBrandSupplier.delete.useMutation({
    onSuccess: () => {
      utils.tradingBrandSupplier.getBrandsBySupplier.invalidate(supplierId);
      showSuccessToast(tCommon('deleted'));
      setDeleteId(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const setPrimaryMutation = trpc.tradingBrandSupplier.setPrimary.useMutation({
    onSuccess: () => {
      utils.tradingBrandSupplier.getBrandsBySupplier.invalidate(supplierId);
      showSuccessToast(tCommon('updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleSetPrimary = (brandId: string) => {
    setPrimaryMutation.mutate({ brandId, supplierId });
  };

  const existingBrandIds = brands?.map((b) => b.brandId) || [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-lg">{t('purchasing.suppliers.brands.title')}</CardTitle>
            <CardDescription>{t('purchasing.suppliers.brands.subtitle')}</CardDescription>
          </div>
          <Button size="sm" onClick={() => setAddModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('purchasing.suppliers.brands.addBrand')}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">{tCommon('loading')}</div>
          ) : !brands || brands.length === 0 ? (
            <div className="text-center py-8">
              <Tags className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-sm font-medium">{t('purchasing.suppliers.brands.noBrands')}</p>
              <p className="text-sm text-muted-foreground">{t('purchasing.suppliers.brands.noBrandsDesc')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {brands.map((brand) => (
                <div
                  key={brand.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {brand.brandLogo ? (
                      <img
                        src={brand.brandLogo}
                        alt={brand.brandName}
                        className="h-10 w-10 rounded-lg object-contain"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                        <Tags className="h-5 w-5 text-emerald-600" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{brand.brandName}</span>
                        {brand.isPrimary && (
                          <Badge variant="default" className="text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            {t('purchasing.suppliers.brands.primary')}
                          </Badge>
                        )}
                        {brand.isAuthorized && (
                          <Badge variant="secondary" className="text-xs">
                            {t('purchasing.suppliers.brands.authorized')}
                          </Badge>
                        )}
                      </div>
                      {brand.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{brand.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!brand.isPrimary && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetPrimary(brand.brandId)}
                        disabled={setPrimaryMutation.isPending}
                        title={t('brands.suppliers.setPrimary')}
                      >
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(brand.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierBrandModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        supplierId={supplierId}
        supplierName={supplierName}
        existingBrandIds={existingBrandIds}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tCommon('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('purchasing.suppliers.brands.removeConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
